import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import Reveal from "./Reveal";
import { useLang } from "../i18n";

const steps = [
  {
    n: "01",
    tk: "pipe.s1t",
    title: "Fundamental Research",
    items: ["Physics", "Mathematics", "Quantum mechanics", "Space-time", "Biology", "Neuroscience", "Psychology", "AI", "Medicine", "Health sciences", "Human performance", "Nutrition"],
    tailK: "pipe.s1tail",
    tail: "and related disciplines.",
  },
  {
    n: "02",
    tk: "pipe.s2t",
    title: "Models & Discovery",
    items: ["Theoretical models", "Scientific hypotheses", "Research papers", "Experimental frameworks", "Data and computational models"],
  },
  {
    n: "03",
    tk: "pipe.s3t",
    title: "Technology",
    items: ["Devices", "Diagnostics", "Biomarkers", "Measurement systems", "Therapeutic technologies", "Human-performance technologies"],
  },
  {
    n: "04",
    tk: "pipe.s4t",
    title: "The Lifespan Labs",
    descK: "pipe.s4d",
    desc: "A curated ecosystem of products and services supporting healthier, longer and better living.",
  },
  {
    n: "05",
    tk: "pipe.s5t",
    title: "The Longer-Life Ecosystem",
    items: ["Health", "Performance", "Prevention", "Recovery", "Nutrition", "Cognition", "Physical wellbeing", "Mental wellbeing"],
    tailK: "pipe.s5tail",
    tail: "and longevity-oriented solutions.",
  },
];

const ResearchPipeline = () => {
  const { t } = useLang();
  return (
    <section id="pipeline" data-testid="pipeline-section" className="relative border-t border-white/5 py-28 md:py-40">
      <div className="mx-auto max-w-4xl px-6">
        <Reveal testId="pipeline-heading" className="text-center">
          <p className="text-gradient mb-6 font-mono text-base uppercase tracking-[0.3em]">
            {t("pipe.eyebrow", "The Pipeline")}
          </p>
          <h2 className="mx-auto max-w-3xl font-display text-[2rem] font-semibold leading-[1.08] tracking-tight sm:text-[2.75rem]">
            {t("pipe.h2a", "From fundamental research to")} <span className="text-gradient">{t("pipe.h2b", "real-world solutions.")}</span>
          </h2>
        </Reveal>

        <div className="mt-16 flex flex-col items-center">
          {steps.map((s, i) => (
            <div key={s.n} className="flex w-full flex-col items-center">
              <motion.div
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`pipeline-step-${s.n}`}
                className="w-full rounded-3xl border border-white/10 bg-surface p-8 transition-colors duration-500 hover:border-cyan-300/30 md:p-10"
              >
                <div className="flex items-baseline gap-4">
                  <span className="text-gradient font-mono text-base tracking-[0.2em]">[{s.n}]</span>
                  <h3 className="font-display text-2xl font-semibold tracking-tight md:text-3xl">{t(s.tk, s.title)}</h3>
                </div>
                {s.items && (
                  <div className="mt-6 flex flex-wrap gap-2.5">
                    {s.items.map((item) => (
                      <span
                        key={item}
                        className="rounded-full border border-white/10 bg-surface px-4 py-2 text-base font-light text-fog"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                )}
                {s.desc && (
                  <p className="mt-6 text-lg font-light leading-relaxed text-fog">{t(s.descK, s.desc)}</p>
                )}
                {s.tail && (
                  <p className="mt-4 text-base font-light text-white/40">{t(s.tailK, s.tail)}</p>
                )}
              </motion.div>
              {i < steps.length - 1 && (
                <ArrowDown className="my-4 h-5 w-5 shrink-0 text-cyan-300/60" />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ResearchPipeline;
